<template>
  <div>
    <TopBar currentTitle="Users" />
    <div class="page-content">
    <h1 class="page-title">Users</h1>
    <p class="page-subtitle">
      <template v-if="isFiltered">{{ filteredUsers.length }} of {{ visibleUsers.length }} users</template>
      <template v-else>{{ visibleUsers.length }} total</template>
    </p>

    <!-- Membership counts, each one a shortcut to the matching filter -->
    <div class="summary-chips mb-4">
      <button v-for="stat in summary" :key="stat.label"
              class="summary-chip"
              :class="[stat.tone, { 'is-selected': membershipFilter === stat.filter }]"
              @click="toggleFilter(stat.filter)">
        <span class="summary-count">{{ stat.count }}</span>
        <span class="summary-label">{{ stat.label }}</span>
      </button>
    </div>

    <!-- Filters Bar -->
    <div class="filters-bar">
      <div class="search-wrapper">
        <b-icon icon="magnify" class="search-icon" />
        <b-input
          placeholder="Search by name, email or ID..."
          v-model="searchQuery"
          expanded>
        </b-input>
      </div>
      <b-select v-model="membershipFilter" placeholder="Membership">
        <option value="">All memberships</option>
        <optgroup label="Grouped">
          <option v-for="stat in quickFilters" :key="stat.filter" :value="stat.filter">
            {{ stat.quickLabel }}
          </option>
        </optgroup>
        <optgroup v-for="group in membershipGroups" :key="group.name" :label="group.name">
          <option v-for="state in group.states" :key="state.key" :value="state.key">
            {{ state.filterLabel }}
          </option>
        </optgroup>
      </b-select>
      <div class="filters-right">
        <span class="hidden-toggles">
          <b-checkbox v-if="anonymousCount" v-model="showAnonymous" size="is-small">
            Show {{ anonymousCount }} anonymous
          </b-checkbox>
          <b-checkbox v-if="mergedCount" v-model="showMerged" size="is-small">
            Show {{ mergedCount }} merged
          </b-checkbox>
        </span>
        <a v-if="isFiltered || showAnonymous || showMerged" class="clear-filters" @click="clearAll">Clear all</a>
        <div class="page-size-selector">
          <span class="page-size-label">Show</span>
          <b-select v-model="perPage" size="is-small" @input="savePerPage">
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </b-select>
        </div>
      </div>
    </div>

    <p v-if="hiddenMatchCount" class="hidden-hint">
      {{ hiddenMatchCount }} hidden anonymous {{ hiddenMatchCount === 1 ? 'profile' : 'profiles' }}
      also {{ hiddenMatchCount === 1 ? 'matches' : 'match' }} —
      <a @click="showHidden">show {{ hiddenMatchCount === 1 ? 'it' : 'them' }}</a>
    </p>

    <!-- Table Card -->
    <div class="feeds-card">
      <div class="feeds-card-body compact">
        <b-table
          :data="filteredUsers"
          :loading="isLoading"
          :hoverable="true"
          :paginated="true"
          :per-page="perPage"
          :current-page.sync="currentPage"
          pagination-position="bottom"
          @click="openUser">

          <b-table-column field="name" label="Name" sortable v-slot="props">
            <p class="has-text-weight-bold">{{ props.row.name || '—' }}</p>
          </b-table-column>

          <b-table-column field="email" label="Email" sortable v-slot="props">
            <span class="email-cell">
              <span v-if="props.row.isAnonymous" class="membership-tag is-anon">Anonymous</span>
              <CopyText v-if="props.row.email || !props.row.isAnonymous"
                        :value="props.row.email" label="Email" />
            </span>
          </b-table-column>

          <!-- Sized for a 28-character Firebase UID; anything longer clips -->
          <b-table-column field="id" label="ID" width="250" v-slot="props">
            <CopyText :value="props.row.id" label="User ID" mono truncate max-width="236px" />
          </b-table-column>

          <b-table-column field="membershipRank" width="150" label="Membership" sortable centered v-slot="props">
            <div class="membership-cell">
              <span class="membership-tag" :class="props.row.membership.tone">
                {{ props.row.membership.label }}
              </span>
              <span v-if="props.row.membership.detail"
                    class="membership-detail"
                    :class="props.row.membership.detailTone">
                {{ props.row.membership.detail }}
              </span>
            </div>
          </b-table-column>

          <b-table-column field="renewalSort" width="130" label="Renews / Expires" sortable centered v-slot="props">
            <span class="date-tag">{{ props.row.renewalLabel }}</span>
          </b-table-column>

          <b-table-column field="createdSort" width="120" label="Created" sortable centered v-slot="props">
            <span class="date-tag">{{ props.row.createdLabel }}</span>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered py-4">
              {{ isLoading ? 'Loading users…' : 'No users found' }}
            </div>
          </template>
        </b-table>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import db from "@/firebase/db"
import CopyText from '@/components/common/CopyText.vue'
import TopBar from '@/components/layout/TopBar.vue'
import {
  formatDate, isAnonymousUser, matchesMembershipFilter, membershipStatus, toDate,
  userAliases, userCreatedAt, userEmail, userName, userUid,
  MEMBERSHIP_STATES, MEMBERSHIP_SUMMARY
} from '@/utils/userFields'

const STORAGE_KEY = 'feeds_per_page_UserListView'
const PAGE_STATE_KEY = 'feeds_list_page_UserListView'
const SCROLL_STATE_KEY = 'feeds_list_state_UserListView'
const SEARCH_STATE_KEY = 'feeds_list_search_UserListView'
const MEMBERSHIP_STATE_KEY = 'feeds_list_membership_UserListView'
const ANONYMOUS_STATE_KEY = 'feeds_list_show_anon_UserListView'
const MERGED_STATE_KEY = 'feeds_list_show_merged_UserListView'

export default {
  name: 'UserListView',
  components: {
    CopyText,
    TopBar
  },
  data() {
    return {
      users: [],
      isLoading: true,
      searchQuery: sessionStorage.getItem(SEARCH_STATE_KEY) || "",
      membershipFilter: sessionStorage.getItem(MEMBERSHIP_STATE_KEY) || "",
      showAnonymous: sessionStorage.getItem(ANONYMOUS_STATE_KEY) === 'true',
      showMerged: sessionStorage.getItem(MERGED_STATE_KEY) === 'true',
      perPage: parseInt(localStorage.getItem(STORAGE_KEY)) || 25,
      currentPage: parseInt(sessionStorage.getItem(PAGE_STATE_KEY)) || 1,
      scrollRestored: false
    }
  },
  watch: {
    searchQuery(value) {
      sessionStorage.setItem(SEARCH_STATE_KEY, value)
      this.currentPage = 1
    },
    membershipFilter(value) {
      sessionStorage.setItem(MEMBERSHIP_STATE_KEY, value)
      this.currentPage = 1
    },
    showAnonymous(value) {
      sessionStorage.setItem(ANONYMOUS_STATE_KEY, value)
      this.currentPage = 1
    },
    showMerged(value) {
      sessionStorage.setItem(MERGED_STATE_KEY, value)
      this.currentPage = 1
    },
    currentPage(newPage) {
      sessionStorage.setItem(PAGE_STATE_KEY, newPage)
    },
    users() {
      // Restore scroll position after data loads
      if (!this.scrollRestored) {
        this.scrollRestored = true
        this.$nextTick(() => {
          const savedState = sessionStorage.getItem(SCROLL_STATE_KEY)
          if (savedState) {
            const state = JSON.parse(savedState)
            if (state.scrollY > 0) {
              setTimeout(() => {
                window.scrollTo(state.scrollX, state.scrollY)
              }, 50)
            }
          }
        })
      }
    }
  },
  created() {
    // Read-only view, so a one-off get rather than a realtime binding on the
    // whole users collection. Sorting is done client side because the
    // created-at field name varies (see utils/userFields).
    db.collection('users')
      .get()
      .then(snapshot => {
        const rows = snapshot.docs
          .map(doc => this.summarise(doc))
          .sort((a, b) => b.createdSort - a.createdSort)
        this.users = this.flagDuplicates(rows)
        this.isLoading = false
      })
      .catch(error => {
        this.isLoading = false
        this.$buefy.toast.open({ type: 'is-danger', message: `Error fetching users: ${error}` })
      })
  },
  computed: {
    // Unclaimed profiles are dropped before anything else counts or filters,
    // so the totals reflect real accounts. Merged wins over plain anonymous,
    // which keeps the two toggles independent of each other.
    visibleUsers() {
      return this.users.filter(user => {
        if (user.isMergedAnonymous) return this.showMerged
        if (user.isAnonymous) return this.showAnonymous
        return true
      })
    },
    filteredUsers() {
      return this.visibleUsers.filter(user => this.matches(user))
    },
    isFiltered() {
      return Boolean(this.searchQuery || this.membershipFilter)
    },
    anonymousCount() {
      return this.users.filter(user => user.isAnonymous && !user.isMergedAnonymous).length
    },
    mergedCount() {
      return this.users.filter(user => user.isMergedAnonymous).length
    },
    // Searching for an id that belongs to a hidden profile would otherwise
    // come back empty with no hint as to why.
    hiddenMatchCount() {
      if (!this.isFiltered) return 0
      const visible = new Set(this.visibleUsers)
      return this.users.filter(user => !visible.has(user) && this.matches(user)).length
    },
    summary() {
      const counts = {}
      this.visibleUsers.forEach(user => {
        counts[user.membership.key] = (counts[user.membership.key] || 0) + 1
      })
      return MEMBERSHIP_SUMMARY
        .map(stat => ({
          ...stat,
          count: stat.keys.reduce((total, key) => total + (counts[key] || 0), 0)
        }))
        .filter(stat => stat.count > 0)
    },
    quickFilters() {
      return MEMBERSHIP_SUMMARY.filter(stat => stat.quickLabel)
    },
    membershipGroups() {
      return MEMBERSHIP_STATES.reduce((groups, state) => {
        const existing = groups.find(group => group.name === state.group)
        if (existing) existing.states.push(state)
        else groups.push({ name: state.group, states: [state] })
        return groups
      }, [])
    }
  },
  methods: {
    // Flatten each doc once so the table can sort and search without
    // re-resolving field names on every render.
    summarise(doc) {
      const data = doc.data()
      const name = userName(data)
      const email = userEmail(data)
      const uid = userUid(data, doc.id)
      const created = userCreatedAt(data)
      const createdDate = toDate(created)
      const membership = membershipStatus(data)
      const isLifetime = membership.key === 'lifetime'
      return {
        id: doc.id,
        uid,
        name,
        email,
        aliases: userAliases(data),
        isAnonymous: isAnonymousUser(data, doc.id),
        isMergedAnonymous: false,
        membership,
        membershipRank: membership.rank,
        renewalLabel: isLifetime ? 'Never' : formatDate(membership.expiryDate),
        // Lifetime entitlements sort as the furthest-away renewal date
        renewalSort: isLifetime
          ? Number.MAX_SAFE_INTEGER
          : (membership.expiryDate ? membership.expiryDate.getTime() : 0),
        createdLabel: formatDate(created),
        createdSort: createdDate ? createdDate.getTime() : 0,
        searchIndex: [name, email, doc.id, uid].filter(Boolean).join(' ').toLowerCase()
      }
    },
    // An anonymous profile that some *other* profile claims as an alias has
    // been superseded: the app writes to the signed-up profile from then on,
    // leaving this doc stale. Self-references are ignored, since a profile's
    // own alias list includes its own id.
    flagDuplicates(rows) {
      const claimed = new Set()
      rows.forEach(row => {
        row.aliases.forEach(alias => {
          if (alias !== row.id && alias !== row.uid) claimed.add(alias)
        })
      })
      rows.forEach(row => {
        row.isMergedAnonymous =
          row.isAnonymous && (claimed.has(row.id) || claimed.has(row.uid))
      })
      return rows
    },
    matches(user) {
      const query = this.searchQuery.trim().toLowerCase()
      return matchesMembershipFilter(user.membership.key, this.membershipFilter) &&
        (!query || user.searchIndex.includes(query))
    },
    showHidden() {
      this.showAnonymous = true
      this.showMerged = true
    },
    savePerPage(value) {
      localStorage.setItem(STORAGE_KEY, value)
    },
    toggleFilter(filter) {
      this.membershipFilter = this.membershipFilter === filter ? '' : filter
    },
    clearAll() {
      this.searchQuery = ''
      this.membershipFilter = ''
      this.showAnonymous = false
      this.showMerged = false
      sessionStorage.removeItem(SEARCH_STATE_KEY)
      this.currentPage = 1
    },
    openUser(user) {
      this.$router.push({ name: 'UserView', params: { id: user.id } })
    }
  }
}
</script>

<style scoped>
.search-wrapper {
  flex: 1;
  max-width: 400px;
  position: relative;
}

.search-wrapper .search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  color: var(--text-muted);
}

.search-wrapper .input {
  padding-left: 40px !important;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size-label {
  font-size: 13px;
  color: var(--text-secondary);
}

.date-tag {
  font-size: 12px;
  color: var(--text-secondary);
}

.summary-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.summary-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 4px 12px;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-full, 999px);
  background: var(--bg-secondary);
  font-family: inherit;
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.summary-chip:hover {
  border-color: var(--border-medium);
}

.summary-chip.is-selected {
  border-color: var(--feeds-green, #00857a);
  background: var(--feeds-green-light, #e6f5f3);
}

.summary-count {
  font-weight: 700;
  color: var(--text-primary);
}

.summary-chip.is-member .summary-count {
  color: var(--feeds-green, #00857a);
}

.summary-chip.is-trial .summary-count,
.summary-chip.is-cancelling .summary-count {
  color: var(--feeds-orange, #ff7849);
}

.membership-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.membership-tag.is-member,
.membership-tag.is-lifetime {
  background: rgba(0, 133, 122, 0.12);
  color: var(--feeds-green, #00857a);
}

.membership-tag.is-trial {
  background: rgba(255, 120, 73, 0.14);
  color: var(--feeds-orange, #ff7849);
}

.membership-tag.is-free,
.membership-tag.is-expired {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

/* Anonymous reads as a state, not a warning — cool tone, away from the
   green/orange the membership pills use */
.membership-tag.is-anon {
  background: rgba(96, 165, 250, 0.16);
  color: #3574d4;
}

.email-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
}

.hidden-toggles {
  display: flex;
  align-items: center;
  gap: 12px;
}

.hidden-hint {
  margin: -8px 0 16px;
  font-size: 13px;
  color: var(--text-secondary);
}

.hidden-hint a {
  cursor: pointer;
}

.membership-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

/* Renew-or-cancel reads as a caption under the state, so the pill stays short */
.membership-detail {
  font-size: 11px;
  color: var(--text-secondary);
}

.membership-detail.is-cancelling {
  color: var(--feeds-orange, #ff7849);
  font-weight: 600;
}
</style>
